// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// COB-499: the shared-vault spec export — everything a member needs to watch or
// recreate their vault outside Cobault, as native Bitcoin infrastructure.
//
// Two importable descriptor forms travel in the file:
//
//   1. `descriptor` — the **sealed** `wsh(sortedmulti(m, <compressed keys>))`
//      the vault actually pays to. Fixed keys, no derivation: Bitcoin Core
//      `importdescriptors` takes it verbatim. Always present.
//   2. `wallet_import_descriptor` — the **ranged** sibling
//      `wsh(sortedmulti(m, [fp/path]xpub/0/*, …))`, rebuilt here from the
//      cosigner account key-origins. Sparrow and friends want the xpub form;
//      its derivation index 0 is byte-identical to the sealed descriptor (each
//      origin's `/0/0` child *is* that cosigner's roster key — the same
//      relationship the key agent's seal-time gate proves). `null` when the
//      vault carries no complete key-origin record (legacy vaults predating
//      HW.13a), in which case only form 1 is usable.
//
// Everything here is pure string work over the already-fetched activation
// bundle — no network, no DOM, no key material. The DOM download itself lives
// with the page section that triggers it.

import type {
  SharedVaultActivationBundle,
  SharedVaultDetailResponse,
  SharedVaultState,
} from './api.js';

// ── BIP-380 descriptor checksum ───────────────────────────────────────

/** BIP-380 `INPUT_CHARSET` — position in this string is the symbol value of a
 *  descriptor character. Order is load-bearing; it is not any conventional
 *  alphabet. Byte-identical to rust-miniscript's `expression::INPUT_CHARSET`,
 *  which is what seals the descriptors this checksum must reproduce. */
const INPUT_CHARSET =
  "0123456789()[],'/*abcdefgh@:$%{}IJKLMNOPQRSTUVWXYZ&+-.;<=>?!^_|~ijklmnopqrstuvwxyzABCDEFGH`#\"\\ ";

/** BIP-380 `CHECKSUM_CHARSET` — the bech32 alphabet, used for the 8 output
 *  characters. */
const CHECKSUM_CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';

/** BIP-380 BCH generator coefficients. The running state is 40 bits, so the
 *  polymod is done in `bigint` — JS bitwise operators truncate to 32. */
const GENERATOR: readonly bigint[] = [
  0xf5dee51989n,
  0xa9fdca3312n,
  0x1bab10e32dn,
  0x3706b1677an,
  0x644d626ffdn,
];

/** One BIP-380 `descsum_polymod` step over a 5-bit symbol. */
function polymodStep(chk: bigint, value: number): bigint {
  const top = chk >> 35n;
  let next = ((chk & 0x7ffffffffn) << 5n) ^ BigInt(value);
  for (let i = 0; i < 5; i++) {
    if ((top >> BigInt(i)) & 1n) next ^= GENERATOR[i];
  }
  return next;
}

/**
 * Compute the 8-character BIP-380 checksum of a descriptor **body** (the part
 * before the `#`). Returns the bare checksum, without the `#`.
 *
 * Mirrors `descsum_create`: each character contributes its low 5 bits as a
 * symbol, and every group of three contributes a further symbol built from the
 * high bits (`cls`), with a short final group flushed as-is.
 *
 * Throws on any character outside `INPUT_CHARSET` — a descriptor that cannot be
 * checksummed is not a descriptor.
 */
export function descriptorChecksum(descriptorBody: string): string {
  let chk = 1n;
  let cls = 0;
  let clscount = 0;
  for (const ch of descriptorBody) {
    const pos = INPUT_CHARSET.indexOf(ch);
    if (pos === -1) {
      throw new Error(`invalid character in descriptor: ${JSON.stringify(ch)}`);
    }
    chk = polymodStep(chk, pos & 31);
    cls = cls * 3 + (pos >> 5);
    if (++clscount === 3) {
      chk = polymodStep(chk, cls);
      cls = 0;
      clscount = 0;
    }
  }
  if (clscount > 0) chk = polymodStep(chk, cls);
  for (let i = 0; i < 8; i++) chk = polymodStep(chk, 0);
  chk ^= 1n;

  let checksum = '';
  for (let i = 0; i < 8; i++) {
    checksum += CHECKSUM_CHARSET[Number((chk >> BigInt(5 * (7 - i))) & 31n)];
  }
  return checksum;
}

// ── Wallet-import (ranged) descriptor ─────────────────────────────────

/** A cosigner key-origin as the backend emits it: `[<8-hex fp>/<path>]<xpub>`,
 *  the extended key already re-versioned to the vault's network (`xpub` on
 *  mainnet, `tpub` on every test net — see `registration.rs`). Hardened
 *  components render `'` from rust-bitcoin; `h` is accepted for tolerance. */
const KEY_ORIGIN_RE =
  /^\[[0-9a-fA-F]{8}(?:\/\d+['h]?)*\](?:x|t)pub[1-9A-HJ-NP-Za-km-z]+$/;

/**
 * Build the ranged, wallet-importable sibling of the sealed descriptor:
 * `wsh(sortedmulti(m, [fp/path]xpub/0/*, …))#checksum`.
 *
 * Returns `null` — never a partial descriptor — unless the bundle's key-origin
 * record is **complete**: one well-formed origin per sealed key (the human
 * roster, plus the verifier key on a policy vault). A legacy vault with no
 * registration record, or one that only half-recorded, gets `null` and the
 * caller falls back to the sealed fixed-key form.
 *
 * The key expressions are sorted lexicographically purely for determinism —
 * `sortedmulti` sorts the derived keys itself, so the emitted order never
 * changes the script or the address.
 */
export function buildWalletImportDescriptor(
  bundle: SharedVaultActivationBundle,
): string | null {
  const origins = bundle.account_key_origins ?? [];
  const expected = bundle.roster.length + (bundle.verifier_pubkey_hex ? 1 : 0);
  if (origins.length !== expected) return null;
  if (!origins.every((o) => KEY_ORIGIN_RE.test(o))) return null;

  const keys = origins.map((o) => `${o}/0/*`).sort();
  const body = `wsh(sortedmulti(${bundle.quorum_m},${keys.join(',')}))`;
  return `${body}#${descriptorChecksum(body)}`;
}

// ── Spec file ─────────────────────────────────────────────────────────

/** The exported file's `format` discriminator. Bump the version suffix on any
 *  key-order or field change so an importer can tell the shapes apart. */
export const SHARED_VAULT_SPEC_FORMAT = 'cobault-shared-vault-spec-v1';

/** Guidance — which of the two descriptor fields a given importer wants. Only
 *  emitted when both forms are present; the origins-incomplete caveat (c)
 *  covers the null case with its own instruction. */
export const SPEC_NOTE_WHICH_DESCRIPTOR =
  'Two importable forms: wallet_import_descriptor is the ranged xpub form ' +
  'for wallet apps that require xpubs (e.g. Sparrow); descriptor is the ' +
  'sealed fixed-key form, importable verbatim with Bitcoin Core ' +
  'importdescriptors.';

/** Caveat (a) — the ranged descriptor derives an unbounded address chain, but
 *  only index 0 is this vault. */
export const SPEC_NOTE_SINGLE_ADDRESS =
  'Only deposit_address belongs to this vault — it is derivation index 0 of ' +
  'wallet_import_descriptor. Cobault and the verifier do not watch any other ' +
  'address this descriptor derives, and coins sent to one will not appear here.';

/** Caveat (b) — no secrets, but not public either. */
export const SPEC_NOTE_CONFIDENTIAL =
  'This file contains no private key material, but it reveals every ' +
  "co-signer's public keys and makes this vault's balance and history " +
  'watchable by anyone who holds it. Treat it as confidential.';

/** Caveat (c) — only when the ranged form could not be built. */
export const SPEC_NOTE_ORIGINS_INCOMPLETE =
  'wallet_import_descriptor is null: this vault has no complete co-signer ' +
  'key-origin record, so the ranged form cannot be rebuilt. Import the ' +
  'fixed-key descriptor field instead (e.g. Bitcoin Core importdescriptors) ' +
  'to watch this vault.';

/**
 * Render the exported spec file body — pretty-printed JSON, 2-space indent,
 * trailing newline. Key order is fixed by the literal below and is part of the
 * `v1` format.
 *
 * Identity and display fields come from the detail response; every field a
 * wallet acts on comes from the **sealed** activation bundle, which is the
 * authoritative record of what was actually committed to.
 */
export function buildSharedVaultSpec(
  vault: SharedVaultDetailResponse,
  bundle: SharedVaultActivationBundle,
): string {
  const walletImport = buildWalletImportDescriptor(bundle);
  const spec = {
    format: SHARED_VAULT_SPEC_FORMAT,
    vault_id: vault.vault_id,
    name: vault.name ?? null,
    network: bundle.network,
    quorum_m: bundle.quorum_m,
    n: vault.n,
    descriptor: bundle.multisig_descriptor,
    wallet_import_descriptor: walletImport,
    deposit_address: bundle.deposit_address,
    deposit_spk: bundle.deposit_spk,
    roster: bundle.roster,
    account_key_origins: bundle.account_key_origins ?? [],
    verifier_pubkey_hex: bundle.verifier_pubkey_hex ?? null,
    contract_hash_hex: bundle.contract_hash_hex ?? null,
    notes: [
      ...(walletImport === null ? [] : [SPEC_NOTE_WHICH_DESCRIPTOR]),
      SPEC_NOTE_SINGLE_ADDRESS,
      SPEC_NOTE_CONFIDENTIAL,
      ...(walletImport === null ? [SPEC_NOTE_ORIGINS_INCOMPLETE] : []),
    ],
  };
  return `${JSON.stringify(spec, null, 2)}\n`;
}

/** Download filename for a vault's spec — short enough to read, long enough to
 *  disambiguate a user's own vaults. */
export function sharedVaultSpecFilename(vaultId: string): string {
  return `cobault-shared-vault-${vaultId.slice(0, 8)}.json`;
}

/** Whether the export may be offered: the descriptor is sealed from `PROPOSED`
 *  onwards, and `GET /shared-vault/{id}/activation` 409s before that. `EMPTY`
 *  still qualifies — a drained vault is exactly when a member wants the backup
 *  artifact. `PENDING_INVITES` (and so every pending-invitee view), `REJECTED`
 *  and `DELETED` have nothing to export. */
export function descriptorExportVisible(
  vault: SharedVaultDetailResponse,
): boolean {
  const sealed: SharedVaultState[] = ['PROPOSED', 'ACTIVE', 'EMPTY'];
  return sealed.includes(vault.state);
}
